import {GoogleMap, Marker} from '@react-google-maps/api'
import React, {memo, useCallback, useState} from 'react'

import {
    EuiButton,
    EuiCallOut,
    htmlIdGenerator
} from '@elastic/eui'

import {useDebounceEffect} from 'utils/useDebounceEffect'

import MapControl from './MapControl'
import {useGetBillboardGeosQuery} from "../../api/rtkQueryApi/ToolsApi";

interface TargetingMapProps {
    suggestedPlaces?: string[]
}

const mapOptions: google.maps.MapOptions = {
    scrollwheel: false,
    streetViewControl: false,
    fullscreenControl: false,
    zoomControl: false,
    rotateControl: false,
    mapTypeControl: false,
    mapTypeId: 'roadmap',
    clickableIcons: false
}

const BillboardFinder: React.FC<TargetingMapProps> = ({suggestedPlaces}) => {
    const [lat, setLat] = useState<number>(35)
    const [lng, setLng] = useState<number>(-99)
    const [radiusMeters, setRadiusMeters] = useState<number>(300000)
    const [mapZoom, setMapZoom] = useState(() => 4)
    const minBillboardZoom = 8

    const getBillboardQuery = useGetBillboardGeosQuery({lat, lng, radiusMeters}, {skip: mapZoom <= minBillboardZoom})
    const [map, setMap] = useState<google.maps.Map | null>(null)
    const [currentCenter, setCurrentCenter] = useState<google.maps.LatLng>()
    const [lastCenter, setLastCenter] = useState<google.maps.LatLng>()
    const [mapCenter, setMapCenter] = useState<google.maps.LatLng>(() => {
        return new google.maps.LatLng(35, -99)
    })


    const fourZoomDegrees = 45
    const [lastBBZoom, setLastBBZoom] = useState<number>(0)

    const handleCenterChanged = () => {
        if (map) {
            const newCenter = map.getCenter()
            setCurrentCenter(newCenter)
            if (!lastCenter) {
                setLastCenter(newCenter)
            }
        }
    }

    const onLoad = useCallback((newMap: google.maps.Map) => {
        if (!map) {
            setMap(newMap)
        }
    }, [])

    const onUnmountMap = useCallback(() => {
        map?.unbindAll()
        setMap(null)
    }, [])

    const onZoomOutClick = () => {
        setMapZoom(mapZoom - 1)
    }

    const onZoomInClick = () => {
        setMapZoom(mapZoom + 1)
    }

    useDebounceEffect(
        200,
        () => {
            if (currentCenter && lastCenter && mapZoom >= minBillboardZoom) {
                const degreeSensitivity = 0.8 / Math.pow(2, mapZoom - minBillboardZoom) > 0.04 ? 0.8 / Math.pow(2, mapZoom - minBillboardZoom) : 0.04
                if (lastBBZoom !== mapZoom || Math.abs(currentCenter.lat() - lastCenter.lat()) > degreeSensitivity || Math.abs(currentCenter.lng() - lastCenter.lng()) > degreeSensitivity) {
                    const radiusMeters = 300000 / Math.pow(2, mapZoom - minBillboardZoom) > 25000 ? 300000 / Math.pow(2, mapZoom - minBillboardZoom) : 25000
                    setLat(currentCenter.lat())
                    setLng(currentCenter.lng())
                    setRadiusMeters(radiusMeters)
                    if (map) {
                        setLastBBZoom(mapZoom)
                        setLastCenter(map.getCenter())
                    }
                }
            }
        },
        [mapZoom, currentCenter, lastCenter]
    )

    useDebounceEffect(
        500,
        () => {
            if (map && suggestedPlaces && suggestedPlaces.length > 0) {
                let latMax = -999
                let latMin = 999
                let lngMax = -999
                let lngMin = 999
                const service = new window.google.maps.places.PlacesService(map)
                let promiseArr: Promise<void>[] = []
                for (let place of suggestedPlaces) {
                    let request = {
                        query: place,
                        fields: ['geometry']
                    }
                    promiseArr.push(
                        new Promise<void>(resolve => {
                            service.findPlaceFromQuery(request, function (results, status) {
                                if (status === google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
                                    const northEast = results[0].geometry?.viewport?.getNorthEast()
                                    const southWest = results[0].geometry?.viewport?.getSouthWest()
                                    latMax = latMax > (northEast?.lat() ?? 0) ? latMax : northEast?.lat() ?? 0
                                    latMin = latMin < (southWest?.lat() ?? 0) ? latMin : southWest?.lat() ?? 0
                                    lngMax = lngMax > (northEast?.lng() ?? 0) ? lngMax : northEast?.lng() ?? 0
                                    lngMin = lngMin < (southWest?.lng() ?? 0) ? lngMin : southWest?.lng() ?? 0
                                }
                                resolve()
                            })
                        })
                    )
                }
                Promise.all(promiseArr).then(() => {
                    const lngRange = Math.abs(latMax - latMin)
                    const latRange = Math.abs(lngMax - lngMin)

                    const maxRange = lngRange > latRange ? lngRange : latRange

                    setMapZoom(Math.floor(4 + Math.log(fourZoomDegrees / maxRange) / Math.log(2)))
                    setMapCenter(new google.maps.LatLng((latMin + latMax) / 2, (lngMax + lngMin) / 2))
                    setCurrentCenter(new google.maps.LatLng((latMin + latMax) / 2, (lngMax + lngMin) / 2))
                })
            }
        },
        [suggestedPlaces, map]
    )

    const renderMap = () => {
        const zoomControl = (
            <React.Fragment>
                <EuiButton className='zoomOut' id={htmlIdGenerator()()} fill size='s' onClick={onZoomOutClick}
                           style={{marginTop: 8, marginRight: 8}}>
                    Zoom Out
                </EuiButton>
                <EuiButton className='zoomIn' id={htmlIdGenerator()()} fill size='s' onClick={onZoomInClick}
                           style={{marginTop: 8, marginRight: 8}}>
                    Zoom In
                </EuiButton>
            </React.Fragment>
        )

        return (
            <GoogleMap mapContainerStyle={{height: 500, width: '100%'}} center={mapCenter} zoom={mapZoom}
                       onLoad={onLoad} onUnmount={onUnmountMap} options={mapOptions}
                       onCenterChanged={handleCenterChanged}>
                <MapControl position={google.maps.ControlPosition.TOP_RIGHT}>{zoomControl}</MapControl>
                {mapZoom >= minBillboardZoom && getBillboardQuery.data && [...getBillboardQuery.data].filter(bb => bb.latitude && bb.longitude).map(bb =>
                    <Marker position={{lat: bb.latitude ?? 0, lng: bb.longitude ?? 0}}/>)}
            </GoogleMap>
        )
    }

    return (
        <React.Fragment>
            {mapZoom < minBillboardZoom &&
                <EuiCallOut size='s' iconType='alert' color='warning' title='Zoom in to view billboard locations'/>}
            <div style={{height: 500, width: '100%'}}>{renderMap()}</div>
        </React.Fragment>
    )
}

export default memo(BillboardFinder)